import { useEffect } from "react";

/**
 * The parts of the Screen Wake Lock API used here. Declared locally: the
 * pinned TypeScript (4.9) ships no `navigator.wakeLock` types.
 */
interface WakeLockSentinelLike {
    released?: boolean;
    release(): Promise<void>;
}
interface WakeLockLike {
    request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

function wakeLockApi(): WakeLockLike | null {
    if (typeof navigator === 'undefined') return null;
    const api = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
    return api && typeof api.request === 'function' ? api : null;
}

/**
 * Keep the screen on while `active` and the page is visible — the camera aim
 * view, where the admin's other hand is on the camera bracket and an unlock
 * is one climb down the ladder.
 *
 * The browser drops a screen lock whenever the page is hidden, so it is taken
 * again on every return to visible. Unsupported browsers (and refusals: low
 * battery, a permissions policy) are silently fine — the page works the same,
 * the screen just may sleep.
 */
export function useScreenWakeLock(active: boolean): void {
    useEffect(() => {
        const api = wakeLockApi();
        if (!active || !api) return undefined;
        let disposed = false;
        let pending = false;
        let sentinel: WakeLockSentinelLike | null = null;

        const acquire = async (): Promise<void> => {
            if (disposed || pending || document.visibilityState !== 'visible') return;
            if (sentinel && !sentinel.released) return;
            pending = true;
            try {
                const next = await api.request('screen');
                if (disposed) {
                    void next.release().catch(() => undefined);
                    return;
                }
                sentinel = next;
            } catch {
                // Refused or unsupported here: the page works without it.
            } finally {
                pending = false;
            }
        };

        const handleVisibility = (): void => {
            if (document.visibilityState === 'visible') void acquire();
        };
        void acquire();
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            disposed = true;
            document.removeEventListener('visibilitychange', handleVisibility);
            if (sentinel && !sentinel.released) void sentinel.release().catch(() => undefined);
            sentinel = null;
        };
    }, [active]);
}
